import { DesignerActionTypes } from '../design/designer-reducer';
import {
  DesignerCommandTypes,
  runDesignerCommand
} from '../design/designer-commands';

export const DESIGNER_CANVAS_COMMAND_NAMES = [
  'setRoofLineLabel',
  'setSelectionMode',
  'addToSelectedObjects',
  'addToSelectedObjectsBulk',
  'removeFromSelectedObjects',
  'clearSelection',
  'setHeight',
  'setPitch',
  'setSetback',
  'deleteSelectedObjects',
  'setSegmentPosition',
  'changeEdgeTypes',
  'setPanelPlacement',
  'moveRoofVertex',
  'setSegmentShadingFactors',
  'setSegmentShadingFactorsServerSide',
  'setShadingTexture',
  'setShadingStatus',
  'setShadingIsRunning',
  'setServerSideShadingIsRunning',
  'setOptPitchAndAzimuth',
  'setSegmentParameters',
  'setDSMParams',
  'automaticAzimuthMode',
  'setPanelsOrientation',
  'setViewMode',
  'setMode',
  'setMoveMode',
  'handleNewShape',
  'handleSaveEdits',
  'handleAddPointToPolygon',
  'handleRemovePointFromPolygon',
  'handleDeleteShape',
  'handleReplaceShapes',
  'handleLineLabel',
  'mapsLink',
  'undo',
  'redo',
  'setHistoryEnabled',
  'setUpdateRGBTextureIsRunning',
  'setAnnotations',
  'newAnnotation',
  'removeAnnotation',
  'handleCanvasData',
  'translateCanvas',
  'setPanelPlacementFillMode',
  'setPanelPlacementLayoutMode',
  'setPanelPlacementOnlySelectedRoof',
  'clearPanels',
  'placeGroundMount'
];

export function selectDesignerCanvasContextState(state = {}, runtime = {}) {
  const installationPackage = state.package || {};
  const property = runtime.property || {};

  return {
    coordinates: state.coordinates,
    resolution: state.resolution ?? property.resolution,
    buffer: state.buffer,
    rowSpacing: state.rowSpacing,
    panelTilt: state.panelTilt,
    azimuthRestriction: state.azimuthRestriction,
    setback: state.setback,
    accordion: state.currentAccordion,
    panelType:
      runtime.panelType ??
      installationPackage.pendingPanel ??
      installationPackage.panel,
    selectionMode: state.selectionMode,
    selectedObjects: state.selectedObjects,
    shadingIsRunning: state.shadingIsRunning,
    serverSideShadingIsRunning: state.serverSideShadingIsRunning,
    optimizer1IsRunning: state.optimizer1IsRunning,
    optimizer2IsRunning: state.optimizer2IsRunning,
    edgeDetectionIsRunning: state.edgeDetectionIsRunning,
    rectificationIsRunning: state.rectificationIsRunning,
    measureIsRunning: state.measureIsRunning,
    panelPlacementIsRunning: state.panelPlacementIsRunning,
    AIIsRunning: state.AIIsRunning,
    treeAIIsRunning: state.treeAIIsRunning,
    optPitchAndAzimuth: runtime.optPitchAndAzimuth ?? property.optimal_roof,
    dsmParams: state.dsmParams ?? property.dsm_params,
    optPOA: runtime.optPOA ?? property.optimal_poa,
    shadingMonth: state.shadingMonth,
    shadingStatus: state.shadingStatus,
    viewMode: state.viewMode,
    iframe: runtime.iframe,
    designFrame: runtime.designFrame,
    mode: state.mode,
    tool: state.tool,
    selectedLabel: state.selectedLabel,
    ios: runtime.ios,
    moveMode: state.moveMode,
    automaticAzimuth: state.automaticAzimuth,
    designType: state.designType,
    updateRGBTextureIsRunning: state.updateRGBTextureIsRunning,
    annotations: state.annotations,
    panelPlacementFillMode: state.panelPlacementFillMode,
    panelPlacementLayoutMode: state.panelPlacementLayoutMode,
    panelPlacementOnlySelectedRoof: state.panelPlacementOnlySelectedRoof
  };
}

function dispatchPatch(dispatch, patch, options) {
  return dispatch?.(
    {
      type: DesignerActionTypes.PATCH,
      patch
    },
    options
  );
}

export function createDesignerCanvasCommands({
  getState,
  dispatch,
  ports = {},
  dispatchOptions = {}
} = {}) {
  const command =
    (name, fallback) =>
    (...args) => {
      if (typeof ports[name] === 'function') return ports[name](...args);
      return fallback?.(...args);
    };
  const localOptions = {
    saveHistory: false,
    iframe: false,
    ...dispatchOptions.local
  };
  const persistedOptions = {
    saveHistory: true,
    iframe: true,
    ...dispatchOptions.persisted
  };
  const dispatchCommand = (designerCommand) => {
    const state = getState?.() || {};
    const commandResult = runDesignerCommand(state, designerCommand);
    const patch = {};
    Object.keys(commandResult.state || {}).forEach((key) => {
      if (commandResult.state[key] !== state[key]) {
        patch[key] = commandResult.state[key];
      }
    });
    if (Object.keys(patch).length) {
      dispatchPatch(dispatch, patch, {
        ...persistedOptions,
        effects: commandResult.effects
      });
    }
    return commandResult;
  };

  return {
    setRoofLineLabel: command('setRoofLineLabel', (index, lineIndex, lines) => {
      if (typeof ports.handleLineLabel === 'function') {
        return ports.handleLineLabel(index, lineIndex, lines);
      }
      return dispatchCommand({
        type: DesignerCommandTypes.LABEL_LINE,
        index,
        lineIndex,
        lines
      });
    }),
    setSelectionMode: command('setSelectionMode', (selectionMode) =>
      dispatchPatch(dispatch, { selectionMode }, localOptions)
    ),
    addToSelectedObjects: command(
      'addToSelectedObjects',
      (index, objectType, removeOld = false) =>
        dispatch?.(
          {
            type: DesignerActionTypes.SELECTION_ADD,
            index,
            objectType,
            removeOld,
            currentAccordion: getState?.()?.currentAccordion
          },
          persistedOptions
        )
    ),
    addToSelectedObjectsBulk: command(
      'addToSelectedObjectsBulk',
      (indices, objectType, clearExisting = false) =>
        dispatch?.(
          {
            type: DesignerActionTypes.SELECTION_ADD,
            index: Array.isArray(indices) ? indices : [indices],
            objectType,
            removeOld: clearExisting,
            currentAccordion: getState?.()?.currentAccordion
          },
          persistedOptions
        )
    ),
    removeFromSelectedObjects: command(
      'removeFromSelectedObjects',
      (index, objectType) =>
        dispatch?.(
          {
            type: DesignerActionTypes.SELECTION_REMOVE,
            index,
            objectType
          },
          persistedOptions
        )
    ),
    clearSelection: command(
      'clearSelection',
      (objectType, forceClear = false) =>
        dispatch?.(
          {
            type: DesignerActionTypes.SELECTION_CLEAR,
            objectType,
            forceClear
          },
          persistedOptions
        )
    ),
    setHeight: command('setHeight'),
    setPitch: command('setPitch'),
    setSetback: command('setSetback'),
    deleteSelectedObjects: command('deleteSelectedObjects'),
    setSegmentPosition: command('setSegmentPosition'),
    changeEdgeTypes: command('changeEdgeTypes'),
    setPanelPlacement: command('setPanelPlacement'),
    moveRoofVertex: command('moveRoofVertex'),
    setSegmentShadingFactors: command('setSegmentShadingFactors'),
    setSegmentShadingFactorsServerSide: command(
      'setSegmentShadingFactorsServerSide'
    ),
    setShadingTexture: command('setShadingTexture', (texture, shadingMonth) =>
      dispatchPatch(
        dispatch,
        {
          showOverlay: true,
          shadingMonth,
          shadingTexture: texture
        },
        persistedOptions
      )
    ),
    setShadingStatus: command('setShadingStatus', (status) =>
      dispatch?.(
        {
          type: DesignerActionTypes.APPLY_SHADING_STATUS,
          status
        },
        persistedOptions
      )
    ),
    setShadingIsRunning: command('setShadingIsRunning', (shadingIsRunning) =>
      dispatchPatch(dispatch, { shadingIsRunning }, localOptions)
    ),
    setServerSideShadingIsRunning: command(
      'setServerSideShadingIsRunning',
      (serverSideShadingIsRunning) =>
        dispatchPatch(dispatch, { serverSideShadingIsRunning }, localOptions)
    ),
    setOptPitchAndAzimuth: command('setOptPitchAndAzimuth'),
    setSegmentParameters: command('setSegmentParameters'),
    setDSMParams: command('setDSMParams'),
    automaticAzimuthMode: command('automaticAzimuthMode'),
    setPanelsOrientation: command('setPanelsOrientation'),
    setViewMode: command('setViewMode', (viewMode) =>
      dispatchPatch(dispatch, { viewMode }, persistedOptions)
    ),
    setMode: command('setMode', (mode) =>
      dispatch?.(
        {
          type: DesignerActionTypes.SET_MODE,
          mode
        },
        persistedOptions
      )
    ),
    setMoveMode: command('setMoveMode', (moveMode) =>
      dispatch?.(
        {
          type: DesignerActionTypes.SET_MOVE_MODE,
          moveMode
        },
        persistedOptions
      )
    ),
    handleNewShape: command('handleNewShape', (shape, scaled = true) =>
      dispatchCommand({
        type: DesignerCommandTypes.NEW_SHAPE,
        shape,
        options: { scaled }
      })
    ),
    handleSaveEdits: command('handleSaveEdits', (updated, scaled = true) =>
      dispatchCommand({
        type: DesignerCommandTypes.SAVE_SHAPE_EDITS,
        updated,
        options: { scaled }
      })
    ),
    handleAddPointToPolygon: command(
      'handleAddPointToPolygon',
      (index, lineIndex, point, scaled = false) =>
        dispatchCommand({
          type: DesignerCommandTypes.ADD_POLYGON_POINT,
          index,
          lineIndex,
          point,
          options: { scaled }
        })
    ),
    handleRemovePointFromPolygon: command(
      'handleRemovePointFromPolygon',
      (index, pointIndex) =>
        dispatchCommand({
          type: DesignerCommandTypes.REMOVE_POLYGON_POINT,
          index,
          pointIndex
        })
    ),
    handleDeleteShape: command('handleDeleteShape', (key) =>
      dispatchCommand({
        type: DesignerCommandTypes.DELETE_SHAPE,
        key
      })
    ),
    handleReplaceShapes: command(
      'handleReplaceShapes',
      (shapes, removeKeys, scaled = true) =>
        dispatchCommand({
          type: DesignerCommandTypes.REPLACE_SHAPES,
          add: Array.isArray(shapes) ? shapes : [shapes],
          remove: Array.isArray(removeKeys) ? removeKeys : [removeKeys],
          options: { scaled }
        })
    ),
    handleLineLabel: command('handleLineLabel', (index, lineIndex, lines) =>
      dispatchCommand({
        type: DesignerCommandTypes.LABEL_LINE,
        index,
        lineIndex,
        lines
      })
    ),
    mapsLink: command('mapsLink'),
    undo: command('undo', () =>
      dispatch?.(
        {
          type: DesignerActionTypes.HISTORY_UNDO
        },
        {
          ...persistedOptions,
          saveHistory: false
        }
      )
    ),
    redo: command('redo', () =>
      dispatch?.(
        {
          type: DesignerActionTypes.HISTORY_REDO
        },
        {
          ...persistedOptions,
          saveHistory: false
        }
      )
    ),
    setHistoryEnabled: command('setHistoryEnabled'),
    setUpdateRGBTextureIsRunning: command(
      'setUpdateRGBTextureIsRunning',
      (updateRGBTextureIsRunning) =>
        dispatchPatch(dispatch, { updateRGBTextureIsRunning }, localOptions)
    ),
    setAnnotations: command('setAnnotations', (annotations) =>
      dispatchCommand({
        type: DesignerCommandTypes.SET_ANNOTATIONS,
        annotations
      })
    ),
    newAnnotation: command(
      'newAnnotation',
      (position, annotationType, params = {}) =>
        dispatchCommand({
          type: DesignerCommandTypes.ADD_ANNOTATION,
          position,
          annotationType,
          params
        })
    ),
    removeAnnotation: command('removeAnnotation', (key) =>
      dispatchCommand({
        type: DesignerCommandTypes.REMOVE_ANNOTATION,
        key
      })
    ),
    handleCanvasData: command('handleCanvasData', (data) =>
      dispatchCommand({
        type: DesignerCommandTypes.IMPORT_CANVAS_DATA,
        data
      })
    ),
    translateCanvas: command('translateCanvas'),
    setPanelPlacementFillMode: command(
      'setPanelPlacementFillMode',
      (panelPlacementFillMode) =>
        dispatch?.(
          {
            type: DesignerActionTypes.PATCH_PLACEMENT_PREFS,
            patch: { panelPlacementFillMode }
          },
          persistedOptions
        )
    ),
    setPanelPlacementLayoutMode: command(
      'setPanelPlacementLayoutMode',
      (panelPlacementLayoutMode) =>
        dispatch?.(
          {
            type: DesignerActionTypes.PATCH_PLACEMENT_PREFS,
            patch: { panelPlacementLayoutMode }
          },
          persistedOptions
        )
    ),
    setPanelPlacementOnlySelectedRoof: command(
      'setPanelPlacementOnlySelectedRoof',
      (onlySelectedRoof) =>
        dispatch?.(
          {
            type: DesignerActionTypes.TOGGLE_SELECTED_ROOF_PLACEMENT,
            onlySelectedRoof
          },
          persistedOptions
        )
    ),
    clearPanels: command('clearPanels'),
    placeGroundMount: command('placeGroundMount')
  };
}

export function createDesignerCanvasContextValue({
  state,
  runtime,
  commands
} = {}) {
  return {
    ...selectDesignerCanvasContextState(state, runtime),
    ...(commands || {})
  };
}
